export type CognitoOidcMetadata = {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
};

const metadataCache = new Map<string, Promise<CognitoOidcMetadata>>();

const normalizeAuthority = (authority: string): string =>
  authority.replace(/\/$/, "");

const parseMetadata = (
  data: unknown,
  authority: string,
): CognitoOidcMetadata => {
  if (typeof data !== "object" || data == null) {
    throw new Error(
      `Cognito OIDC discovery for ${authority} returned invalid JSON`,
    );
  }
  const o = data as Record<string, unknown>;
  if (
    typeof o.authorization_endpoint !== "string" ||
    typeof o.token_endpoint !== "string" ||
    typeof o.issuer !== "string"
  ) {
    throw new Error(
      `Cognito OIDC discovery for ${authority} is missing required endpoints`,
    );
  }
  return {
    issuer: o.issuer,
    authorization_endpoint: o.authorization_endpoint,
    token_endpoint: o.token_endpoint,
    end_session_endpoint:
      typeof o.end_session_endpoint === "string"
        ? o.end_session_endpoint
        : undefined,
  };
};

/** Browser-only: fetch Cognito metadata from `{authority}/.well-known/openid-configuration`. */
export const fetchCognitoOidcMetadata = (
  authority: string,
): Promise<CognitoOidcMetadata> => {
  const key = normalizeAuthority(authority);
  const cached = metadataCache.get(key);
  if (cached != null) {
    return cached;
  }
  const promise = (async () => {
    const res = await fetch(`${key}/.well-known/openid-configuration`);
    if (!res.ok) {
      throw new Error(
        `Cognito OIDC discovery failed with HTTP ${String(res.status)}`,
      );
    }
    let json: unknown;
    try {
      json = await res.json();
    } catch (e) {
      throw new Error("Cognito OIDC discovery response was not valid JSON", {
        cause: e,
      });
    }
    return parseMetadata(json, key);
  })();
  metadataCache.set(key, promise);
  return promise;
};

/** Test helper: clear in-memory discovery cache. */
export const clearCognitoOidcMetadataCacheForTests = (): void => {
  metadataCache.clear();
};
